import React, { useState, useEffect } from 'react';
import { Star, Plus, Pencil, Trash2, Save, X, ArrowLeft, Lock, MessageSquare, Image, Youtube, ClipboardList } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import GalleryAdmin from './GalleryAdmin';
import VideoAdmin from './VideoAdmin';
import RequestsAdmin from './RequestsAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || '';

const AdminPanel = ({ onBack }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('requests'); // 'requests', 'reviews', 'gallery', or 'videos'
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    date: new Date().toISOString().split('T')[0],
    text: ''
  });

  // Check if already authenticated in this session
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReviews();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      toast.success('Autentificare reușită!');
    } else {
      toast.error('Parolă incorectă!');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
    onBack();
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reviews?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Eroare la încărcarea recenziilor');
    } finally {
      setLoading(false);
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#2196f3] to-[#5cd26f] rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-[#0d2e5d]">Panou Admin</CardTitle>
            <p className="text-gray-600 mt-2">Introdu parola pentru a continua</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Parolă"
                className="text-center text-lg"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Înapoi
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#2196f3] hover:bg-[#1976d2]"
                >
                  Intră
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const seedReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/seed`, { method: 'POST' });
      if (response.ok) {
        toast.success('Recenziile inițiale au fost adăugate');
        fetchReviews();
      }
    } catch (error) {
      toast.error('Eroare la adăugarea recenziilor inițiale');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.text || !formData.date) {
      toast.error('Completează toate câmpurile obligatorii');
      return;
    }

    try {
      const url = editingId 
        ? `${API_URL}/api/reviews/${editingId}`
        : `${API_URL}/api/reviews`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingId ? 'Recenzie actualizată!' : 'Recenzie adăugată!');
        resetForm();
        fetchReviews();
      } else {
        throw new Error('Failed to save review');
      }
    } catch (error) {
      toast.error('Eroare la salvarea recenziei');
    }
  };

  const handleEdit = (review) => {
    setFormData({
      name: review.name,
      rating: review.rating,
      date: review.date,
      text: review.text
    });
    setEditingId(review.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ești sigur că vrei să ștergi această recenzie?')) return;

    try {
      const response = await fetch(`${API_URL}/api/reviews/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Recenzie ștearsă');
        fetchReviews();
      }
    } catch (error) {
      toast.error('Eroare la ștergerea recenziei');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rating: 5,
      date: new Date().toISOString().split('T')[0],
      text: ''
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const renderStars = (rating, interactive = false) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 cursor-pointer transition-colors ${
          index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
        }`}
        onClick={() => interactive && setFormData({ ...formData, rating: index + 1 })}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2196f3]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="hover:bg-gray-200"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#0d2e5d]">Panou Admin</h1>
              <p className="text-gray-600">Gestionează conținutul site-ului</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" className="text-red-600 hover:bg-red-50">
            Deconectare
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('requests')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === 'requests'
                ? 'text-[#5cd26f] border-[#5cd26f]'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            Solicitări
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === 'reviews'
                ? 'text-[#2196f3] border-[#2196f3]'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Recenzii
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === 'gallery'
                ? 'text-[#2196f3] border-[#2196f3]'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <Image className="w-4 h-4" />
            Galerie
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === 'videos'
                ? 'text-[#2196f3] border-[#2196f3]'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <Youtube className="w-4 h-4" />
            Videoclipuri
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'requests' ? (
          <RequestsAdmin />
        ) : activeTab === 'gallery' ? (
          <GalleryAdmin />
        ) : activeTab === 'videos' ? (
          <VideoAdmin />
        ) : (
          <>
            {/* Reviews Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#0d2e5d]">Recenzii</h2>
              <div className="flex gap-2">
                {reviews.length === 0 && (
                  <Button onClick={seedReviews} variant="outline">
                    Adaugă recenzii demo
                  </Button>
                )}
                <Button onClick={() => setShowAddForm(true)} className="bg-[#5cd26f] hover:bg-[#4bc160]">
                  <Plus className="w-4 h-4 mr-2" />
                  Adaugă Recenzie
                </Button>
              </div>
            </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="mb-8 border-2 border-[#2196f3]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingId ? 'Editează Recenzia' : 'Adaugă Recenzie Nouă'}</span>
                <Button variant="ghost" size="icon" onClick={resetForm}>
                  <X className="w-5 h-5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nume Client *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="ex: Maria Popescu"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data Recenziei *
                    </label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rating
                  </label>
                  <div className="flex gap-1">
                    {renderStars(formData.rating, true)}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Text Recenzie *
                  </label>
                  <textarea
                    value={formData.text}
                    onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    placeholder="Scrie recenzia aici..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2196f3] focus:border-transparent"
                    required
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Anulează
                  </Button>
                  <Button type="submit" className="bg-[#2196f3] hover:bg-[#1976d2]">
                    <Save className="w-4 h-4 mr-2" />
                    {editingId ? 'Salvează Modificările' : 'Adaugă Recenzia'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Reviews List */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-700">
            Recenzii ({reviews.length})
          </h2>
          
          {reviews.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500">Nu există recenzii încă.</p>
              <p className="text-gray-400 text-sm mt-2">
                Adaugă prima recenzie sau folosește butonul "Adaugă recenzii demo".
              </p>
            </Card>
          ) : (
            reviews.map((review) => (
              <Card key={review.id} className={`transition-all ${!review.is_active ? 'opacity-50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#2196f3] to-[#5cd26f] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {review.avatar}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-[#0d2e5d]">{review.name}</span>
                          <span className="text-sm text-gray-500">
                            {new Date(review.date).toLocaleDateString('ro-RO', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        <div className="flex gap-0.5 mb-2">
                          {renderStars(review.rating)}
                        </div>
                        <p className="text-gray-700">{review.text}</p>
                        {!review.is_active && (
                          <span className="inline-block mt-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                            Inactiv
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(review)}
                        className="hover:bg-blue-50 hover:text-[#2196f3]"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(review.id)}
                        className="hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Instructions */}
        <Card className="mt-8 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-[#0d2e5d] mb-2">Cum să adaugi o recenzie de pe Google Maps:</h3>
            <ol className="list-decimal list-inside text-gray-700 space-y-1 text-sm">
              <li>Deschide profilul tău Google Business</li>
              <li>Copiază numele clientului, data și textul recenziei</li>
              <li>Apasă "Adaugă Recenzie" și completează câmpurile</li>
              <li>Recenzia va apărea automat pe site</li>
            </ol>
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
